import { useRef, useState } from 'react'

import { deleteRentalPortalListingPhoto } from '../../../../../../api'
import { EMPTY_LIST } from '../../../../../../constants/common'
import { useNotification } from '../../../../../../hooks'
import {
  getRentalPortalListingPhotosState,
  saveRentalPortalListingPhotos,
} from '../../../../RentalPortalPhotoGallery'
import {
  getHasRemovedPersistedListingPhoto,
  getHasUnsavedListingPhotoChanges,
  getListingPhotosWithoutFile,
} from '../../RentalPortalListingProfilePhotoEditorModal.utils'

export function useListingProfilePhotoEditorModal({
  initialPhotos,
  listingId,
  onClose,
  onReload,
  onSave,
}) {
  const { handleAddErrorNotification } = useNotification()

  const [photos, setPhotos] = useState(initialPhotos)

  const [uploadingFiles, setUploadingFiles] = useState(EMPTY_LIST)

  const [isSubmitting, setIsSubmitting] = useState(false)

  const [isCloseConfirm, setIsCloseConfirm] = useState(false)

  const hasPersistedWriteRef = useRef(false)

  const savedPhotosRef = useRef(initialPhotos)

  function handleChangePhotos(nextPhotos) {
    if (getHasRemovedPersistedListingPhoto({ nextPhotos, photos })) {
      hasPersistedWriteRef.current = true
    }

    setPhotos(nextPhotos)
  }

  async function handleDeletePhoto(fileId) {
    await deleteRentalPortalListingPhoto({ fileId, id: listingId })

    savedPhotosRef.current = getListingPhotosWithoutFile(
      savedPhotosRef.current,
      fileId
    )
  }

  async function syncPhotosWithListing() {
    const state = await getRentalPortalListingPhotosState({
      listingId,
      photos,
    })

    savedPhotosRef.current = state.savedPhotos

    setPhotos(state.photos)
  }

  async function handleSubmit() {
    setIsSubmitting(true)

    try {
      await saveRentalPortalListingPhotos({
        listingId,
        photos,
        savedPhotos: savedPhotosRef.current,
      })

      onSave()
    } catch {
      handleAddErrorNotification('RentalPortal.ListingPhotoSaveErrorKey')

      hasPersistedWriteRef.current = true

      await syncPhotosWithListing().catch(console.error)

      onReload()

      setIsSubmitting(false)
    }
  }

  function handleFinishSession() {
    if (hasPersistedWriteRef.current) {
      onReload()
    }

    onClose()
  }

  function handleClose() {
    if (!isSubmitting) {
      if (
        getHasUnsavedListingPhotoChanges({
          initialPhotos: savedPhotosRef.current,
          photos,
          uploadingFiles,
        })
      ) {
        setIsCloseConfirm(true)
      } else {
        handleFinishSession()
      }
    }
  }

  function handleCancelClose() {
    setIsCloseConfirm(false)
  }

  function handleConfirmClose() {
    setIsCloseConfirm(false)

    handleFinishSession()
  }

  return {
    handleCancelClose,
    handleChangePhotos,
    handleClose,
    handleConfirmClose,
    handleDeletePhoto,
    handleSubmit,
    isCloseConfirm,
    isSubmitting,
    photos,
    setUploadingFiles,
    uploadingFiles,
  }
}
